import { AfterViewInit, Component, EventEmitter, Input, NgZone, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormField } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatAutocomplete } from '@angular/material/autocomplete';

export enum AutoCompletePanelBehaviour {
  OpenOnClick = 1,
  OpenWhenTyping,
}
@Component({
  selector: 'abcAutoCompleteInput',
  styles: [
    '.autoCompleteOption { height: 30px; line-height: 30px; }',
    `
      ::ng-deep .mat-mdc-autocomplete-panel {
        padding: 0 !important;
        overflow: hidden !important;
        max-height: 180px !important;
      }
    `,
    `
      cdk-virtual-scroll-viewport {
        height: 180px; /* 6 × 30px */
        width: 100%;
      }
    `,
    `
      cdk-virtual-scroll-viewport .cdk-virtual-scroll-content-wrapper {
        width: 100%;
      }
    `,
  ],
  template: `
    <mat-form-field #searchFormField [class]="class" floatLabel="never" style="width: 100%">
      <input
        #searchInput
        type="search"
        matInput
        placeholder="{{ placeHolder }}"
        [(ngModel)]="value"
        (mousedown)="searchInputMouseDown($event)"
        (click)="searchInputClick($event)"
        [formControl]="searchInputFC"
        (keydown)="searchInputKeyDown($event, auto)"
        [matAutocomplete]="auto"
        spellcheck="false"
      />
      @if (searchInput.value) {
        <mat-icon (click)="searchInputClear($event, searchInput)" svgIcon="clear" class="iconClear"></mat-icon>
      }
      <mat-autocomplete #auto="matAutocomplete" (optionSelected)="optionSelected()" class="{{ id }}">
        <cdk-virtual-scroll-viewport itemSize="30" style="height: 250px">
          <mat-option
            *cdkVirtualFor="let option of filteredAutocompleteOptions | async"
            [value]="option"
            class="autoCompleteOption"
          >
            <p [innerHTML]="option | highlight: searchInput.value"></p>
          </mat-option>
        </cdk-virtual-scroll-viewport>
      </mat-autocomplete>
    </mat-form-field>
  `,
  standalone: false,
})
export class AbcAutoCompleteInputComponent implements OnInit, AfterViewInit {
  filteredAutocompleteOptions: Observable<string[]>;
  private focusedItemIndex: number = -1;
  private originalFilterValue: string;
  private isFilterArrowMoving: boolean;
  searchInputFC = new UntypedFormControl();
  @ViewChild('searchFormField', { static: true }) searchFormField: MatFormField;
  @ViewChild('searchInput', { static: false }) searchInput: any;
  @ViewChild(MatAutocompleteTrigger, { static: true }) autocompleteTrigger: MatAutocompleteTrigger;
  @Input() id: string;
  @Input() placeHolder: string;
  @Input() class: string;
  @Input() items: string[];
  @Input() small: boolean;
  @Input() panelBehaviour: AutoCompletePanelBehaviour;
  @Input() panelDigitsToOpen: number;
  @Input() panelMaxItemCount: number;
  @Input() lockedDigits: number;
  @Input() value: string;
  @Input() clearAfterSubmit: boolean;
  @Input() hideUnderline: boolean;
  @Input() closePanelAfterSubmit: boolean;
  @Output() submit = new EventEmitter<string>();

  @ViewChild('auto') autocomplete: MatAutocomplete;
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

  constructor(
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    private ngZone: NgZone,
  ) {
    this.panelBehaviour = AutoCompletePanelBehaviour.OpenOnClick;
    this.panelDigitsToOpen = 1;
    this.panelMaxItemCount = 200;
    this.lockedDigits = 0;
    this.clearAfterSubmit = true;
    //is: baseline-clear-24px.svg
    iconRegistry.addSvgIconLiteral(
      'clear',
      sanitizer.bypassSecurityTrustHtml(
        '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/><path d="M0 0h24v24H0z" fill="none"/></svg>',
      ),
    );
  }

  searchInputClick(e) {
    e.stopPropagation();
    this.autocompleteTrigger.autocompleteDisabled = false;

    if (this.panelBehaviour === AutoCompletePanelBehaviour.OpenOnClick) {
      this.autocompleteTrigger.openPanel();
    }
  }

  searchInputMouseDown(e) {
    this.autocompleteTrigger.autocompleteDisabled = true;
  }

  ngOnInit() {
    this.filteredAutocompleteOptions = this.searchInputFC.valueChanges.pipe(
      startWith(''),
      //   pairwise(),
      //take max of 20 results (avoid a bunch of html)
      // map(([prev, next]: [string, string]) => {
      map((val) => {
        const filterValue: string = this.isFilterArrowMoving ? this.originalFilterValue : val;
        if (
          filterValue &&
          filterValue.length >= this.panelDigitsToOpen &&
          this.panelBehaviour === AutoCompletePanelBehaviour.OpenWhenTyping &&
          !this.autocompleteTrigger.panelOpen
        ) {
          this.autocompleteTrigger.openPanel();
        }
        if (
          this.items &&
          (this.panelBehaviour === AutoCompletePanelBehaviour.OpenOnClick ||
            filterValue.length >= this.panelDigitsToOpen)
        ) {
          const search = filterValue?.toLowerCase();
          const result: string[] = [];
          if (this.items && search) {
            for (const option of this.items) {
              if (option?.toLowerCase()?.includes(search)) {
                result.push(option);
                if (this.panelMaxItemCount > 0 && result.length >= this.panelMaxItemCount) {
                  break;
                }
              }
            }
          }
          return result;
        } else {
          return [];
        }
      }),
    );
  }

  ngAfterViewInit() {
    if (this.small) {
      this.styleToSmallFormField(this.searchFormField);
    }
    this.autocomplete.opened.subscribe(() => {
      // Nach Panel-Animation Größe neu berechnen
      this.ngZone.onStable.pipe(take(1)).subscribe(() => {
        this.viewport?.checkViewportSize();
      });
    });
  }

  searchInputKeyDown(event, autoComplete) {
    switch (event.key) {
      case 'Enter': {
        if (this.searchInputFC.value && this.searchInputFC.value !== '') {
          this.submitCurrentValue();
          if (this.closePanelAfterSubmit) {
            this.autocompleteTrigger.closePanel();
          }
        }
        break;
      }
      case 'ArrowDown':
      case 'ArrowUp': {
        if (!this.isFilterArrowMoving) {
          this.originalFilterValue = this.searchInputFC.value;
        }
        this.isFilterArrowMoving = true;
        const currOptions = autoComplete.options.toArray();
        if (currOptions.length === 0) {
          break;
        }
        if (event.key === 'ArrowDown') {
          this.focusedItemIndex++;
        } else {
          this.focusedItemIndex--;
        }
        this.focusedItemIndex =
          ((this.focusedItemIndex % currOptions.length) + currOptions.length) % currOptions.length;
        this.searchInputFC.setValue(currOptions[this.focusedItemIndex].value);
        break;
      }
      case 'Backspace':
      case 'Delete': {
        if (this.lockedDigits > 0) {
          const allowedLength = event.key === 'Delete' ? this.lockedDigits - 1 : this.lockedDigits;
          if (this.searchInput.nativeElement.selectionStart <= allowedLength) {
            if (this.searchInput.nativeElement.selectionEnd > this.searchInput.nativeElement.selectionStart) {
              this.searchInput.nativeElement.selectionStart = this.lockedDigits;
            } else {
              event.preventDefault();
            }
          }
        }
        this.isFilterArrowMoving = false;
        this.focusedItemIndex = -1;
        break;
      }
      default: {
        this.isFilterArrowMoving = false;
        this.focusedItemIndex = -1;
        break;
      }
    }
  }

  searchInputClear(event, searchInputRef) {
    this.isFilterArrowMoving = false;
    this.focusedItemIndex = -1;

    searchInputRef.focus();
    this.searchInputFC.setValue('');
    event.stopPropagation();
  }

  private styleToSmallFormField(formField: MatFormField) {
    const formFieldWrapperEl: Element =
      formField?._elementRef?.nativeElement?.getElementsByClassName('mat-form-field-wrapper')[0];
    if (formFieldWrapperEl) {
      const formFieldUnderlineEl: Element = formFieldWrapperEl.getElementsByClassName('mat-form-field-underline')[0];
      const formFieldInfixEl: Element = formFieldWrapperEl.getElementsByClassName('mat-form-field-infix')[0];

      formFieldWrapperEl.setAttribute('style', 'padding-bottom: 0px !important;');

      if (formFieldUnderlineEl) {
        let underlineStyle = 'bottom: 0px !important;';
        if (this.hideUnderline) {
          underlineStyle += 'display: none; !important';
        }
        formFieldUnderlineEl.setAttribute('style', underlineStyle);
      }

      if (formFieldInfixEl) {
        formFieldInfixEl.setAttribute('style', 'border-top: unset !important;');
      }
    }
  }

  optionSelected() {
    this.submitCurrentValue();
  }

  closePanel() {
    this.autocompleteTrigger.closePanel();
  }

  private submitCurrentValue() {
    const rawValue = this.searchInputFC.value;
    const valueToEmit = typeof rawValue === 'string' ? rawValue : this.extractSubmitValue(rawValue);

    if (typeof valueToEmit === 'string') {
      this.submit.emit(valueToEmit);
    }

    if (this.clearAfterSubmit) {
      this.searchInputFC.setValue('');
    }
  }

  protected extractSubmitValue(rawValue: any): string | null {
    return null;
  }
}
